import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { CashForecastPoint } from '../../hooks/useKPIs'

interface CashForecastChartProps {
  data: CashForecastPoint[]
}

function formatEurK(value: number): string {
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000)     return `€${(value / 1_000).toFixed(0)}k`
  return `€${value.toFixed(0)}`
}

// "2026-03-24" → "24 Mar" oppure "2026-W12" → "Sett 12"
function formatWeek(week: string): string {
  if (week.includes('W')) {
    const wNum = week.split('-W')[1]
    return `Sett ${parseInt(wNum, 10)}`
  }
  try {
    const d = new Date(week)
    return `${d.getDate()} ${['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'][d.getMonth()]}`
  } catch {
    return week
  }
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-xl border border-[--km-border] bg-[#141b28] px-4 py-3 shadow-xl text-sm">
      <p className="text-[--km-text-secondary] mb-2 font-medium">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-sm" style={{ background: entry.color }} />
          <span className="text-[--km-text-secondary]">{entry.name}:</span>
          <span className="text-[--km-text-primary] font-semibold tabular-nums">
            {formatEurK(entry.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 py-10">
      <p className="text-sm text-[--km-text-muted]">Nessun dato forecast disponibile.</p>
      <p className="text-xs text-[--km-text-muted]">
        Carica fatture e scadenziari per visualizzare il cash forecast.
      </p>
    </div>
  )
}

export function CashForecastChart({ data }: CashForecastChartProps) {
  if (!data.length) return <EmptyState />

  const chartData = data.map((d) => ({
    ...d,
    settimana: formatWeek(d.week),
  }))

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />

        <XAxis
          dataKey="settimana"
          tick={{ fill: 'var(--km-text-muted)', fontSize: 10 }}
          axisLine={false}
          tickLine={false}
          dy={8}
          interval="preserveStartEnd"
        />

        <YAxis
          tickFormatter={formatEurK}
          tick={{ fill: 'var(--km-text-muted)', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          width={44}
        />

        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />

        {/* Inflow settimanale — barre */}
        <Bar
          dataKey="expected_inflow"
          name="Incasso atteso"
          fill="#6366f1"
          fillOpacity={0.8}
          radius={[3, 3, 0, 0]}
          maxBarSize={24}
        />

        {/* Cumulativo — linea */}
        <Line
          type="monotone"
          dataKey="cumulative"
          name="Cumulativo"
          stroke="#a855f7"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, fill: '#a855f7', stroke: '#fff', strokeWidth: 1.5 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}
